import React from "react";
import { motion } from "framer-motion";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { cardVariants, hoverEffects } from "@/lib/animations";

export type HoverEffect = "lift" | "glow" | "scale" | "none";

export interface AnimatedCardProps {
  children: React.ReactNode;
  className?: string;
  /** Hover effect type */
  hoverEffect?: HoverEffect;
  /** Whether this card is inside a StaggerContainer */
  isStaggerChild?: boolean;
}

/**
 * AnimatedCard - 带动画效果的卡片组件
 *
 * 支持多种悬停效果：
 * - lift: 上浮效果（适合痛点卡片）
 * - glow: 发光效果（适合证言卡片）
 * - scale: 缩放效果（适合功能卡片图标）
 * - none: 无悬停效果
 *
 * @example
 * ```tsx
 * // 独立使用
 * <AnimatedCard hoverEffect="lift">
 *   <CardContent />
 * </AnimatedCard>
 *
 * // 在 StaggerContainer 中使用
 * <StaggerContainer>
 *   <AnimatedCard isStaggerChild hoverEffect="glow">
 *     <CardContent />
 *   </AnimatedCard>
 * </StaggerContainer>
 * ```
 */
export function AnimatedCard({
  children,
  className,
  hoverEffect = "lift",
  isStaggerChild = false,
}: AnimatedCardProps) {
  const prefersReducedMotion = useReducedMotion();

  // 无障碍降级：直接渲染无动画
  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>;
  }

  const getHoverStyle = () => {
    switch (hoverEffect) {
      case "lift":
        return hoverEffects.lift;
      case "glow":
        return hoverEffects.glow;
      case "scale":
        return hoverEffects.scale;
      case "none":
      default:
        return undefined;
    }
  };

  // 如果是 StaggerContainer 的子元素，使用 variants
  if (isStaggerChild) {
    return (
      <motion.div
        variants={cardVariants}
        whileHover={getHoverStyle()}
        className={className}
      >
        {children}
      </motion.div>
    );
  }

  // 独立使用时，不需要 variants
  return (
    <motion.div
      whileHover={getHoverStyle()}
      transition={{ duration: 0.2 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default AnimatedCard;
